import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { InfoOutlined, ReceiptOutlined } from '@mui/icons-material'
import React from 'react'
import ReactPaginate from 'react-paginate'
import { pagedList } from 'src/viewModels/pagedList'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { inject } from 'mobx-react'
import { IStores } from 'src/Stores'
import moment from 'moment'
import { InboxItemType } from 'src/utils/InboxItemType'
import { WorkflowHistory } from 'src/viewModels/workflow/WorkflowHistory'
import {
  getBilloutHistory,
  getCaseHistory,
  getClaimHistory,
  getDocumentHistory,
  getQuoteHistory,
  getInvoiceHistory,
} from 'src/services/WorkflowService'
import { SearchRequestBase } from 'src/viewModels/SearchRequestBase'

interface IActionLogModalProps {
  isOpen: boolean
  closeModal: () => void
  associatedItemId: string
  inboxType: InboxItemType
}

class ActionLogModal extends React.Component<IActionLogModalProps> {
  public async componentDidMount(): Promise<void> {
    await this.getActionLogData()
  }

  private setPaginationOffsetData = () => {
    const { page, totalItems, pageSize } = this.state
    const firstRecordIndex = totalItems > 0 ? (page - 1) * pageSize + 1 : 0
    const lastRecordIndex = page * pageSize < totalItems ? page * pageSize : totalItems
    this.setState({
      firstRecordIndex: firstRecordIndex,
      lastRecordIndex: lastRecordIndex,
    })
  }

  private getActionLogData = async () => {
    this.setState({ isLoading: true })
    const { page, pageSize } = this.state
    const data: SearchRequestBase = {
      page,
      pageSize,
    }

    switch (this.props.inboxType) {
      case InboxItemType.Document:
        await getDocumentHistory(this.props.associatedItemId, data).then(
          async (response: any) => {
            this.setState(
              {
                gridData: response.items,
                pageCount: response.totalPages,
                totalItems: response.totalItems,
              },
              () => {
                this.setPaginationOffsetData()
              }
            )
          }
        )
        break
      case InboxItemType.Claim:
        await getClaimHistory(this.props.associatedItemId, data).then(
          async (response: any) => {
            this.setState(
              {
                gridData: response.items,
                pageCount: response.totalPages,
                totalItems: response.totalItems,
              },
              () => {
                this.setPaginationOffsetData()
              }
            )
          }
        )
        break
      case InboxItemType.Quote:
        await getQuoteHistory(this.props.associatedItemId, data).then(
          async (response: any) => {
            this.setState(
              {
                gridData: response.items,
                pageCount: response.totalPages,
                totalItems: response.totalItems,
              },
              () => {
                this.setPaginationOffsetData()
              }
            )
          }
        )
        break
      case InboxItemType.Billout:
        await getBilloutHistory(this.props.associatedItemId, data).then(
          async (response: any) => {
            this.setState(
              {
                gridData: response.items,
                pageCount: response.totalPages,
                totalItems: response.totalItems,
              },
              () => {
                this.setPaginationOffsetData()
              }
            )
          }
        )
        break
      case InboxItemType.Case:
        await getCaseHistory(this.props.associatedItemId, data).then(
          async (response: any) => {
            this.setState(
              {
                gridData: response.items,
                pageCount: response.totalPages,
                totalItems: response.totalItems,
              },
              () => {
                this.setPaginationOffsetData()
              }
            )
          }
        )
        break
      case InboxItemType.Invoice:
        await getInvoiceHistory(this.props.associatedItemId, data).then(
          async (response: any) => {
            this.setState(
              {
                gridData: response.items,
                pageCount: response.totalPages,
                totalItems: response.totalItems,
              },
              () => {
                this.setPaginationOffsetData()
              }
            )
          }
        )
        break
      default:
        break
    }
    this.setState({ isLoading: false })
  }

  private handlePageChange = (event: any) => {
    const page = event.selected
    this.setState({ page: page + 1 }, () => this.getActionLogData())
  }

  private isGridDataEmpty = () => {
    return this.state.totalItems === 0
  }

  private mapLogGridData = () => {
    if (this.isGridDataEmpty()) {
      return (
        <TableRow className="gridNoPadding" key={1}>
          <TableCell colSpan={10} align="center">
            No records found
          </TableCell>
        </TableRow>
      )
    } else {
      return this.state.gridData?.map((record, index) => (
        <TableRow className="gridPadding" key={index}>
          <TableCell align="right">{record.workflowRun}</TableCell>
          <TableCell>{record.inboxName}</TableCell>
          <TableCell align="right">
            {record.actionDate ? moment(record.actionDate).format('MM/DD/YYYY') : ''}
          </TableCell>
          <TableCell>{record.actionUser}</TableCell>
          <TableCell>
            {record.actionName}&nbsp;
            {record.actionDescription && (
              <Tooltip title={record.actionDescription}>
                <InfoOutlined fontSize="small" />
              </Tooltip>
            )}
          </TableCell>
          <TableCell>
            {record.actionAssignUser && (
              <Typography>
                ASSIGNMENT:&nbsp;
                <strong>{record.actionAssignUser}</strong>
                {record.actionAssignDueDate && (
                  <i>
                    &nbsp;with due date&nbsp;
                    {moment(record.actionAssignDueDate).format('MM/DD/YYYY')}
                  </i>
                )}
                <br />
              </Typography>
            )}
            {record.actionPendDate && (
              <Typography>
                PEND:&nbsp;
                <strong>{moment(record.actionPendDate).format('MM/DD/YYYY')}</strong>
                &nbsp;<i>{record.actionPendNote}</i>
                <br />
              </Typography>
            )}
            {record.actionAssociatedItemDescription && (
              <Typography>
                ASSOCIATED ITEM:&nbsp;
                <strong>{record.actionAssociatedItemDescription}</strong>
                <br />
              </Typography>
            )}
            {record.comment && (
              <Box
                sx={{
                  fontFamily: 'Arial, sans-serif',
                  fontSize: '14px',
                  whiteSpace: 'pre-line',
                  '& table': {
                    borderCollapse: 'collapse !important',
                    width: '100% !important',
                  },
                  '& th': {
                    border: '1px solid #ddd !important',
                    padding: '8px !important',
                    backgroundColor: '#f2f2f2 !important',
                    fontWeight: 'bold !important',
                    color: '#000 !important',
                    fontFamily: 'Arial, sans-serif !important',
                    fontSize: '14px !important',
                  },
                  '& thead th:nth-of-type(5), & thead th:nth-of-type(6)': {
                    textAlign: 'right !important',
                  },
                  '& td': {
                    border: '1px solid #ddd !important',
                    padding: '8px !important',
                    fontFamily: 'Arial, sans-serif !important',
                    fontSize: '14px !important',
                  },
                  '& tbody td:nth-of-type(5), & tbody td:nth-of-type(6)': {
                    textAlign: 'right !important',
                  },
                  '& tbody tr:last-child td:nth-of-type(2)': {
                    textAlign: 'right !important',
                  },
                  '& tfoot td': {
                    border: '1px solid #000 !important',
                    fontWeight: 'bold !important',
                    backgroundColor: '#fafafa !important',
                  },
                }}
                dangerouslySetInnerHTML={{ __html: record.comment }}
              />
            )}
          </TableCell>
        </TableRow>
      ))
    }
  }

  private paginationInfo = () => {
    if (!this.isGridDataEmpty()) {
      return (
        <div className="pagination">
          <Typography variant="caption" gutterBottom>
            Showing {this.state.firstRecordIndex} to {this.state.lastRecordIndex} of{' '}
            {this.state.totalItems} entries
          </Typography>
        </div>
      )
    } else {
      return <></>
    }
  }

  public state = {
    gridData: new pagedList().items as WorkflowHistory[],
    page: 1,
    pageCount: 1,
    firstRecordIndex: 0,
    lastRecordIndex: 0,
    totalItems: 0,
    pageSize: 10,
    isLoading: true,
  }

  public render() {
    return (
      <>
        <Dialog
          fullWidth={true}
          maxWidth="xl"
          open={this.props.isOpen!}
          onClose={(_event, reason) => {
            if (reason === 'backdropClick') return
            this.props.closeModal()
          }}
        >
          <DialogTitle style={{ padding: 0 }}>
            <ResponsiveAppBar
              title={'Workflow Action Log'}
              pageIcon={<ReceiptOutlined />}
              isModal={true}
            />
          </DialogTitle>
          <DialogContent style={{ padding: '12px 12px 8px' }}>
            {this.state.isLoading && (
              <div
                style={{
                  position: 'fixed',
                  display: 'flex',
                  width: '100%',
                  height: '100%',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  cursor: 'pointer',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: '1200',
                }}
              >
                <CircularProgress size={100} />
              </div>
            )}
            <Grid
              item
              container
              direction="row"
              justifyContent="space-between"
              alignContent="center"
              alignItems="flex-start"
              wrap="wrap"
            >
              <Grid item xs={12} style={{ minWidth: '750px' }}>
                <Paper>
                  <Table aria-label="simple table">
                    <TableHead>
                      <TableRow className="gridPadding">
                        <TableCell align="right">Workflow Run</TableCell>
                        <TableCell>Inbox</TableCell>
                        <TableCell align="right">Performed On</TableCell>
                        <TableCell>Performed By</TableCell>
                        <TableCell>Action</TableCell>
                        <TableCell>Comments</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody style={{ width: '100%' }}>
                      {this.mapLogGridData()}
                    </TableBody>
                  </Table>
                </Paper>
                <div className="pagination-row">
                  {this.paginationInfo()}
                  <ReactPaginate
                    previousLabel={'<'}
                    nextLabel={'>'}
                    onPageChange={this.handlePageChange}
                    forcePage={this.state.page - 1}
                    pageCount={this.state.pageCount}
                    containerClassName={'pagination'}
                    activeClassName={'active'}
                    //@ts-ignore
                    renderOnZeroPageCount={null}
                  />
                </div>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.props.closeModal} data-cy="close">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </>
    )
  }
}

const InjectedActionLogModal = inject<
  IStores,
  IActionLogModalProps,
  Partial<IActionLogModalProps>,
  any
>(() => ({}))(ActionLogModal)

export default InjectedActionLogModal
